import type { EnrichedSpot, WindHour } from "../../lib/types";
import { degreesToCompass } from "../../lib/scorer";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { CompassRing } from "./CompassRing";
import { SpotDetailMap } from "./SpotDetailMap";
import styles from "./SpotMapCard.module.css";

/** Google Maps' universal directions URL — both mobile platforms launch
 *  the native Maps app when it's installed, and desktop falls through to
 *  the web view. Simpler than UA-sniffing iOS (`maps://`) vs Android
 *  (`geo:`) and works everywhere. */
function directionsUrl(lat: number, lon: number): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`;
}

interface SpotMapCardProps {
  spot: EnrichedSpot;
  /** The forecast hour driving the compass + wind summary. */
  heroHour: WindHour | null;
}

/** Spot map card — Figma 150-5146. Map (16:10 aspect) with the compass
 *  overlay, plus a wind summary line below. Wind data is driven by the
 *  caller-supplied `heroHour` (typically the user's selected forecast hour). */
export function SpotMapCard({ spot, heroHour }: SpotMapCardProps) {
  return (
    <section className={styles.mapCard}>
      <div className={styles.mapFrame}>
        <SpotDetailMap
          coords={spot.coords}
          windDirectionDeg={heroHour?.dirDeg ?? null}
          windSpeedMs={heroHour?.speedMs ?? null}
          zoom={11}
        />
        <div className={styles.compassOverlay}>
          <CompassRing
            windDirectionDeg={heroHour?.dirDeg ?? null}
            windSpeedMs={heroHour?.speedMs ?? null}
            ideal={spot.ideal}
            secondary={spot.secondary}
          />
        </div>
      </div>
      {heroHour && (
        <div className={styles.mapWindSummary}>
          <span>
            {(heroHour.speedMs * 1.944).toFixed(0)} kn ·{" "}
            <span className={styles.gusts}>
              {(heroHour.gustMs * 1.944).toFixed(0)} gusts
            </span>
          </span>
          <span className={styles.mapWindSummaryRight}>
            {degreesToCompass(heroHour.dirDeg)} · {Math.round(heroHour.dirDeg)}°
          </span>
        </div>
      )}
      <Button
        className={styles.directionsButton}
        onClick={() =>
          window.open(
            directionsUrl(spot.coords.lat, spot.coords.lon),
            "_blank",
            "noopener,noreferrer",
          )
        }
        aria-label={`Open directions to ${spot.name} in Maps`}
      >
        <Icon name="directions" />
        Directions to spot
      </Button>
    </section>
  );
}
